import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector, useDispatch } from "react-redux";
import { GET_JWT } from "services/apiURl";
import { changeLanguage } from "store/actions";
import { getCookie, setJWTTokens } from "utils/tokens";
import { useTranslation } from "react-i18next";

import PrivateRoutes from "utils/PrivateRoutes";

// components
import Header from "components/Header";

// containers
const Home = lazy(() => import("containers/Home"));
const Login = lazy(() => import("containers/Login"));
const AccessPhoto = lazy(() => import("containers/AccessPhoto"));
const OrgAlbums = lazy(() => import("containers/OrgAlbums"));
const ProductDetails = lazy(() => import("containers/ProductDetails"));
const MyProfileScreen = lazy(() => import("containers/MyProfileScreen"));
const ViewCart = lazy(() => import("containers/ViewCart"));
const Checkout = lazy(() => import("containers/CheckOut"));
const PayboxPayment = lazy(() => import("containers/PayboxPayment"));
const Faq = lazy(() => import("containers/Faq"));
const OrderSuccess = lazy(() => import("components/OrderSuccess/OrderSuccess"));
const OrderFailure = lazy(() => import("components/OrderFailure/OrderFailure"));

import "styles/common.scss";
import api, { scheduleAccessTokenRefresh } from "utils/request";
import { commonHeaders } from "services/httpHelper";
import useScreenMobile from "hooks/useScreenMobile";

const App = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useScreenMobile({ size: 768 });
  const isProductDetailsPage = location.pathname.includes("/product-details/");

  const [headerVisible, setHeaderVisible] = useState(false);
  const [animateArrow, setAnimateArrow] = useState(true);
  const [showMoveUpMessage, setShowMoveUpMessage] = useState(true);

  const syncTokensFromCookiesToLocalStorage = () => {
    const accessJWT = getCookie("accessJWT");
    const refreshJWT = getCookie("refreshJWT");

    if (accessJWT && !localStorage.getItem("access_token")) {
      localStorage.setItem("access_token", accessJWT);
    }

    if (refreshJWT && !localStorage.getItem("refresh_token")) {
      localStorage.setItem("refresh_token", refreshJWT);
    }

    if (accessJWT) {
      setJWTTokens({ access: accessJWT, refresh: refreshJWT });
      scheduleAccessTokenRefresh();
    }
  };

  useEffect(() => {
    if (isProductDetailsPage && !isMobile && !headerVisible) {
      const timer = setTimeout(() => {
        setAnimateArrow(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isProductDetailsPage, isMobile, headerVisible]);

  useEffect(() => {
    if (isProductDetailsPage && !isMobile) {
      const timer = setTimeout(() => {
        setShowMoveUpMessage(false);
      }, 5000);

      return () => clearTimeout(timer);
    } else {
      setShowMoveUpMessage(false);
    }
  }, [isProductDetailsPage, isMobile]);


  useEffect(() => {
    if (isProductDetailsPage && !isMobile) {
      const handleMouseMove = (e) => {
        const header = document.querySelector(".header");

        if (!header) return;

        const headerRect = header.getBoundingClientRect();
        const mouseInHeader =
          e.clientY >= headerRect.top && e.clientY <= headerRect.bottom;

        if (e.clientY < 10 || mouseInHeader) {
          setHeaderVisible(true);
        } else {
          setHeaderVisible(false);
        }
      };

      window.addEventListener("mousemove", handleMouseMove);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
      };
    } else {
      setHeaderVisible(true);
    }
  }, [isProductDetailsPage, isMobile]);


  const fetchJwtToken = async () => {
    try {
      const response = await api.get(`${GET_JWT}`, {
        headers: commonHeaders
      });
      setJWTTokens(response.data);
      scheduleAccessTokenRefresh();
    } catch (error) {
      console.error("Error fetching JWT token:", error);
    }
  };

  const profileData = useSelector((store) => store.profileData);
  if (
    profileData &&
    profileData.email &&
    !localStorage.getItem("access_token")
  ) {
    fetchJwtToken();
  }

  // if (localStorage.getItem("access_token") && (profileData === null)) {
  //   dispatch(fetchProfileDetails());
  // }

  useEffect(() => {
    dispatch(changeLanguage("fr"));
  }, []);

  syncTokensFromCookiesToLocalStorage();

  return (
    <>
      <BrowserRouter>
        <Header isVisible={headerVisible} />
        {isProductDetailsPage && !isMobile && !headerVisible && showMoveUpMessage && (
          <div
            style={{
              position: "fixed",
              opacity: showMoveUpMessage ? 1 : 0,
              top: "10px",
              left: "50%",
              transform: "translateX(-50%)",
              background: "#e6f0ff",
              color: "#003366",
              padding: "6px 14px",
              borderRadius: "20px",
              display: "flex",
              alignItems: "center",
              fontSize: "13px",
              fontWeight: "500",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              zIndex: 999,
              gap: "8px",
              transition: "opacity 0.5s ease"
            }}
          >
            <div
              style={{
                display: "inline-block",
                animation: animateArrow
                  ? "bounceUpDown 1s infinite alternate"
                  : "none"
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#003366"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="19" x2="12" y2="5"></line>
                <polyline points="5 12 12 5 19 12"></polyline>
              </svg>
            </div>
            {t("productDetails.headerTopMsg")}
          </div>
        )}

        <Suspense
          fallback={
            <div style={{ padding: "2rem", textAlign: "center" }}>
              Loading...
            </div>
          }
        >
          <Routes>
            <Route element={<PrivateRoutes />}>
              <Route path="/" element={<Navigate to="/parents" replace />} />
              <Route path="/parents" element={<AccessPhoto />} />
              <Route
                path="/parents/:accessId/:accessKey"
                element={<AccessPhoto />}
              />
              <Route path="/parents/home" element={<Home />} />
              <Route path="/parents/albums" element={<OrgAlbums />} />
              <Route path="/parents/home/:orgSlug" element={<Home />} />
              <Route path="/parents/login" element={<Login />} />

              <Route
                path="/parents/product-details/:orgSlug/:eventSlug"
                element={<ProductDetails headerVisible={headerVisible} />}
              />
              <Route
                path="/parents/profile-screen"
                element={<MyProfileScreen />}
              />
              <Route path="/parents/view-cart" element={<ViewCart />} />
              <Route path="/parents/checkout" element={<Checkout />} />
              <Route
                path="/parents/paybox-payment"
                element={<PayboxPayment />}
              />
              <Route
                path="/parents/lemonway-payment"
                element={<PayboxPayment />}
              />
              <Route
                path="/parents/payment-success"
                element={<OrderSuccess />}
              />
              <Route
                path="/parents/payment-failure"
                element={<OrderFailure />}
              />
              <Route
                path="/parents/payment/paybox-success"
                element={<PayboxPayment />}
              />
              <Route
                path="/parents/payment/paybox-failure"
                element={<PayboxPayment />}
              />
              <Route
                path="/parents/payment/stripe-success"
                element={<PayboxPayment />}
              />
              <Route
                path="/parents/payment/stripe-failure"
                element={<PayboxPayment />}
              />
              <Route
                path="/parents/payment/lemonway-success"
                element={<PayboxPayment />}
              />
              <Route
                path="/parents/payment/lemonway-failure"
                element={<PayboxPayment />}
              />
              <Route path="/parents/faq" element={<Faq />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
      <ToastContainer
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default App;
